'use client'

import { CloseRounded } from '@mui/icons-material'
import { Box, IconButton, Stack } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Colors, Typography } from '@ui'
import {
  useWatchParty,
  useWatchPartyDismiss,
  useWatchPartyDismissReset,
  useWatchPartyPrompt,
} from 'app/_hooks'
import { watchPartyHref } from 'app/lib/watchParty'
import { Route } from 'next'
import Link from 'next/link'
import { type MouseEvent, useLayoutEffect, useRef, useState } from 'react'
import { useRepliesQuery } from '~/features/video_detail/hooks/replies/useCommentReplies'
import { useMediaQuery } from '~/hooks/useMediaQuery'

const TICKER_SPEED_PX_PER_SECOND = 60
const TICKER_GAP = 4
const TICKER_LOOP_GAP = 12
const TICKER_REPLY_LIMIT = 10

type WatchPartyTickerItem = {
  type: 'count' | 'text' | 'title'
  key: string
  text: string
}

const useTickerItems = (
  commentId: string | undefined,
  withTitle: boolean,
): WatchPartyTickerItem[] => {
  const { prompt } = useWatchPartyPrompt(commentId)
  const { data } = useRepliesQuery(commentId ?? '', !commentId)

  if (!prompt) {
    return []
  }

  const replies = (data?.commentReplies?.edges ?? [])
    .flatMap((edge) => (edge.node && !edge.node.isSpoiler ? [edge.node] : []))
    .slice(0, TICKER_REPLY_LIMIT)

  return [
    ...(withTitle ? [{ type: 'title' as const, key: 'title', text: '' }] : []),
    { type: 'text', key: `prompt-${prompt.id}`, text: prompt.contents ?? '' },
    {
      type: 'count',
      key: `count-${prompt.id}`,
      text: `コメント${data?.commentReplies?.totalCount ?? 0}件`,
    },
    ...replies.map((reply) => ({
      type: 'text' as const,
      key: `reply-${reply.id}`,
      text: reply.contents ?? '',
    })),
  ]
}

const LABEL_BACKGROUND = alpha(Colors['Samansa_Yellow/900'], 0.72)
const TICKER_BACKGROUND = Colors['Black/Alpha/72']

const BannerTitle = () => (
  <>
    <Typography name='Body/14/Bold' color='Base/White'>
      みんなで観よう！
    </Typography>
    <Typography name='Body/14/Bold' color='Samansa_Yellow/500'>
      ウォッチパーティー
      <Box component='span' sx={{ color: Colors['Base/White'] }}>
        開催中🎉
      </Box>
    </Typography>
  </>
)

const TickerItem = ({ item }: { item: WatchPartyTickerItem }) => {
  if (item.type === 'title') {
    return (
      <Stack direction='row' sx={{ flexShrink: 0 }}>
        <BannerTitle />
      </Stack>
    )
  }

  if (item.type === 'count') {
    return (
      <Box
        sx={{
          flexShrink: 0,
          padding: '4px 16px',
          borderRadius: '999px',
          backgroundColor: Colors['Samansa_Yellow/500'],
        }}
      >
        <Typography name='Body/14/Bold' color='Base/Black'>
          {item.text}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Typography name='Body/14/Medium' color='Base/White'>
        {item.text}
      </Typography>
    </Box>
  )
}

const Ticker = ({ items }: { items: WatchPartyTickerItem[] }) => {
  const loopRef = useRef<HTMLDivElement>(null)
  const [duration, setDuration] = useState(0)
  const itemKeys = items.map((item) => item.key).join()

  useLayoutEffect(() => {
    if (loopRef.current) {
      setDuration(loopRef.current.offsetWidth / TICKER_SPEED_PX_PER_SECOND)
    }
  }, [itemKeys])

  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        backgroundColor: TICKER_BACKGROUND,
        paddingLeft: { xs: 2, sm: TICKER_GAP },
        '@keyframes watch-party-ticker': {
          from: { transform: 'translateX(0)' },
          to: { transform: 'translateX(-50%)' },
        },
      }}
    >
      <Stack
        direction='row'
        sx={{
          flexShrink: 0,
          width: 'max-content',
          minWidth: '200%',
          animation: duration ? `watch-party-ticker ${duration}s linear infinite` : 'none',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {[0, 1].map((loop) => (
          <Stack
            key={loop}
            ref={loop === 0 ? loopRef : undefined}
            direction='row'
            alignItems='center'
            gap={TICKER_GAP}
            sx={{ flexShrink: 0, flexGrow: 1, paddingRight: TICKER_LOOP_GAP }}
          >
            {items.map((item) => (
              <TickerItem key={item.key} item={item} />
            ))}
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

type Props = {
  overlay?: boolean
  onClick?: () => void
}

export const WatchPartyBanner = ({ overlay = false, onClick }: Props) => {
  const watchParty = useWatchParty()
  useWatchPartyDismissReset()
  const { dismiss, isDismissed } = useWatchPartyDismiss()
  const { isSm } = useMediaQuery()
  const hidden = !watchParty || isDismissed(watchParty.key)
  const items = useTickerItems(hidden ? undefined : watchParty?.promptCommentIds[0], !isSm)

  if (!watchParty || hidden || items.length === 0) {
    return null
  }

  return (
    <Stack
      direction='row'
      alignItems='stretch'
      sx={{
        width: '100%',
        overflow: 'hidden',
        ...(overlay
          ? { position: 'absolute', top: 0, left: 0, zIndex: 3 }
          : { backgroundColor: Colors['Base/Black'] }),
      }}
    >
      <Stack
        component={Link}
        href={watchPartyHref(watchParty.videoId) as Route}
        onClick={
          onClick &&
          ((event: MouseEvent) => {
            event.preventDefault()
            onClick()
          })
        }
        direction='row'
        alignItems='stretch'
        sx={{
          flexGrow: 1,
          minWidth: 0,
          overflow: 'hidden',
          textDecoration: 'none',
          cursor: 'pointer',
        }}
      >
        <Stack
          justifyContent='center'
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexShrink: 0,
            padding: '8px 16px',
            backgroundColor: LABEL_BACKGROUND,
          }}
        >
          <BannerTitle />
        </Stack>

        <Ticker items={items} />
      </Stack>

      <IconButton
        onClick={() => dismiss(watchParty.key)}
        aria-label='close'
        sx={{
          flexShrink: 0,
          color: Colors['White/Alpha/56'],
          backgroundColor: TICKER_BACKGROUND,
          borderRadius: 0,
          '&:hover': {
            color: Colors['Base/White'],
            backgroundColor: TICKER_BACKGROUND,
          },
        }}
      >
        <CloseRounded />
      </IconButton>
    </Stack>
  )
}
